import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import gsap from 'gsap';
import { setUserDetails } from '../../action/SpinToWinAction';
import SpinHeader from '../SpinHeader';
import Button from '../Button/Button';
import { SimpleFloatingLabel } from '../FloatingLabel/floatingLabel';
import SpinWheel from '../SpinWheel';
import classes from './GetStarted.module.css';

const PersonIcon = () => (
  <svg width="17" height="21" viewBox="0 0 17 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.999 4.5C11.999 5.49456 11.6039 6.44839 10.9007 7.15165C10.1974 7.85491 9.24356 8.25 8.249 8.25C7.25444 8.25 6.30061 7.85491 5.59735 7.15165C4.89409 6.44839 4.499 5.49456 4.499 4.5C4.499 3.50544 4.89409 2.55161 5.59735 1.84835C6.30061 1.14509 7.25444 0.75 8.249 0.75C9.24356 0.75 10.1974 1.14509 10.9007 1.84835C11.6039 2.55161 11.999 3.50544 11.999 4.5ZM0.75 18.618C0.782135 16.6504 1.58634 14.7742 2.98918 13.394C4.39202 12.0139 6.28109 11.2405 8.249 11.2405C10.2169 11.2405 12.106 12.0139 13.5088 13.394C14.9117 14.7742 15.7159 16.6504 15.748 18.618C13.3954 19.6968 10.8371 20.2535 8.249 20.25C5.573 20.25 3.033 19.666 0.75 18.618Z"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const StoreIcon = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.9977 18.75V11.25C11.9977 11.0511 12.0767 10.8603 12.2173 10.7197C12.358 10.579 12.5488 10.5 12.7477 10.5H15.7477C15.9466 10.5 16.1373 10.579 16.278 10.7197C16.4187 10.8603 16.4977 11.0511 16.4977 11.25V18.75M11.9977 18.75H0.857671M11.9977 18.75H16.4977M16.4977 18.75H20.1377M18.7477 18.75V7.099M18.7477 7.099C18.1478 7.4451 17.4466 7.57304 16.7631 7.46113C16.0796 7.34922 15.4559 7.00435 14.9977 6.485C14.4477 7.107 13.6437 7.5 12.7477 7.5C12.3219 7.50041 11.901 7.41 11.513 7.23479C11.125 7.05959 10.7789 6.80363 10.4977 6.484C9.94767 7.107 9.14367 7.5 8.24767 7.5C7.82195 7.50041 7.40104 7.41 7.01304 7.23479C6.62504 7.05959 6.27888 6.80363 5.99767 6.484C5.53959 7.00351 4.91591 7.34858 4.23242 7.46067C3.54892 7.57276 2.84769 7.44498 2.24767 7.099M18.7477 7.099C19.1463 6.86891 19.4866 6.55019 19.7423 6.16751C19.998 5.78484 20.1622 5.34846 20.2222 4.89215C20.2823 4.43584 20.2365 3.97184 20.0885 3.53605C19.9405 3.10026 19.6942 2.70436 19.3687 2.379L18.1787 1.19C17.8976 0.908615 17.5164 0.75035 17.1187 0.75H3.87567C3.47812 0.750085 3.09687 0.907982 2.81567 1.189L1.62667 2.379C1.30191 2.70474 1.05629 3.1007 0.90874 3.53637C0.761194 3.97204 0.715667 4.43577 0.775667 4.89182C0.835668 5.34786 0.999594 5.78403 1.25481 6.16671C1.51002 6.54939 1.84971 6.86835 2.24767 7.099M2.24767 18.75V7.099M5.24767 15.75H8.99767C9.19658 15.75 9.38735 15.671 9.528 15.5303C9.66865 15.3897 9.74767 15.1989 9.74767 15V11.25C9.74767 11.0511 9.66865 10.8603 9.528 10.7197C9.38735 10.579 9.19658 10.5 8.99767 10.5H5.24767C5.04876 10.5 4.85799 10.579 4.71734 10.7197C4.57669 10.8603 4.49767 11.0511 4.49767 11.25V15C4.49767 15.414 4.83367 15.75 5.24767 15.75Z"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface Errors {
  firstName?: string;
  lastName?: string;
  storeNumber?: string;
}

export interface UserDetails {
  firstName: string;
  lastName: string;
  storeNumber: string;
}

interface GetStartedProps {
  isActive?: boolean;
  /**
   * Hands the validated details to the parent so it can run the step 2 → 3
   * transition and commit them when the animation lands. Without it the
   * details are dispatched immediately and the step advances at once.
   */
  onSubmitTransition?: (details: UserDetails) => void;
}

const GetStarted: FC<GetStartedProps> = ({ isActive, onSubmitTransition }) => {
  const dispatch = useDispatch();
  const [ firstName, setFirstName ] = useState('');
  const [ lastName, setLastName ] = useState('');
  const [ storeNumber, setStoreNumber ] = useState('');
  const [ errors, setErrors ] = useState<Errors>({});
  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isActive) return;
    // The step 2 → 3 crossfade leaves this page and its wheel at opacity 0 on
    // purpose; drop that when the page becomes active so a replayed flow shows.
    gsap.set([ '.gsap-gs-root', '.gsap-gs-wheel', '.gsap-gs-card' ], { clearProps: 'opacity' });
    firstNameRef.current?.focus();
  }, [ isActive ]);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const storeNumberRef = useRef<HTMLInputElement>(null);

  const validate = (): boolean => {
    const next: Errors = {};
    if (!firstName.trim()) next.firstName = 'First name is required.';
    if (!lastName.trim()) next.lastName = 'Last name is required.';
    if (!storeNumber.trim()) next.storeNumber = 'Store number is required.';
    setErrors(next);
    if (next.firstName) firstNameRef.current?.focus();
    else if (next.lastName) lastNameRef.current?.focus();
    else if (next.storeNumber) storeNumberRef.current?.focus();
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const details = { firstName, lastName, storeNumber };
    if (onSubmitTransition) onSubmitTransition(details);
    else dispatch(setUserDetails(details));
  };

  return (
    <div className={`${classes.root} gsap-gs-root`}>
      <div className={`${classes.headerContainer} gsap-gs-fade`}>
        <SpinHeader/>
      </div>

      <h1 className={`${classes.title} gsap-gs-fade`}>LET'S GET STARTED</h1>
      <p className={`${classes.subtitle} gsap-gs-fade`}>Enter your details below to begin the challenge.</p>

      <div className={classes.formWrapper}>
        <div className="gsap-gs-wheel">
          <SpinWheel spinMs={200} spinning={false} rotation={0} size={380} />
        </div>

        <form className={`${classes.card} gsap-gs-fade gsap-gs-card`} onSubmit={handleSubmit}>
          <div className={classes.fieldWrap}>
            <SimpleFloatingLabel label="First Name" value={firstName} onChange={v => {
              setFirstName(v);
              setErrors(p => ({ ...p, firstName: undefined }));
            }} icon={<PersonIcon/>} inputRef={firstNameRef}/>
            {errors.firstName && <p className={classes.error}>{errors.firstName}</p>}
          </div>
          <div className={classes.fieldWrap}>
            <SimpleFloatingLabel label="Last Name" value={lastName} onChange={v => {
              setLastName(v);
              setErrors(p => ({ ...p, lastName: undefined }));
            }} icon={<PersonIcon/>} inputRef={lastNameRef}/>
            {errors.lastName && <p className={classes.error}>{errors.lastName}</p>}
          </div>
          <div className={classes.fieldWrap}>
            <SimpleFloatingLabel label="Store Number" value={storeNumber} onChange={v => {
              setStoreNumber(v);
              setErrors(p => ({ ...p, storeNumber: undefined }));
            }} icon={<StoreIcon/>} type="tel" inputRef={storeNumberRef}/>
            {errors.storeNumber && <p className={classes.error}>{errors.storeNumber}</p>}
          </div>

          <div className={classes.btnRow}>
            <Button type="submit" className={classes.btn}>Submit</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GetStarted;
